import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { invoiceService } from "../services/invoice.service";
import { cleanDocument } from "@/shared/lib/document";
import type {
  InvoiceCategory,
  InvoiceResponseDTO,
  InvoiceSearchParams,
} from "../types/invoice.types";

interface UseInvoiceListOptions {
  category: InvoiceCategory;
  enabled: boolean;
  pageSize?: number;
  initialFilters?: InvoiceListFilters;
}

export interface InvoiceListFilters {
  type?: InvoiceSearchParams["type"];
  status?: InvoiceSearchParams["status"];
  paymentMethod?: InvoiceSearchParams["paymentMethod"];
  externalId?: string;
  value?: number;
  rentalId?: number;
  graduationId?: number;
  contractNumber?: string;
  clientName?: string;
  clientDocument?: string;
}

const DEFAULT_PAGE_SIZE = 10;

function normalizeClientFilters(
  clientName: string | undefined,
  clientDocument: string | undefined,
): Pick<InvoiceSearchParams, "clientName" | "clientDocument"> {
  const name = clientName?.trim();
  const document = clientDocument ? cleanDocument(clientDocument) : "";
  return {
    ...(name ? { clientName: name } : {}),
    ...(document ? { clientDocument: document } : {}),
  };
}

export function useInvoiceList({
  category,
  enabled,
  pageSize = DEFAULT_PAGE_SIZE,
  initialFilters,
}: UseInvoiceListOptions) {
  const [data, setData] = useState<InvoiceResponseDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [filters, setFilters] = useState<InvoiceListFilters>(
    () => initialFilters ?? {},
  );
  const requestIdRef = useRef(0);
  // Controller da última request em voo. Ao disparar uma nova, aborta a
  // anterior — cancela na rede, não só ignora a resposta (requestIdRef).
  const controllerRef = useRef<AbortController | null>(null);

  const load = useCallback(
    async (targetPage: number) => {
      const requestId = ++requestIdRef.current;
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      setLoading(true);
      setError(null);
      try {
        const { clientName, clientDocument, ...directFilters } = filters;
        const searchParams: InvoiceSearchParams = {
          ...directFilters,
          ...normalizeClientFilters(clientName, clientDocument),
          category,
          page: targetPage,
          size: pageSize,
          sortBy: "issuedAt",
          sortDirection: "DESC",
        };
        const response = await invoiceService.search(searchParams, {
          signal: controller.signal,
        });
        if (requestIdRef.current !== requestId) return;
        setData(response.content);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
        setPage(targetPage);
      } catch (error: unknown) {
        if (controller.signal.aborted) return;
        if (requestIdRef.current !== requestId) return;
        const message = getApiErrorMessage(
          error,
          "Erro ao carregar notas fiscais",
        );
        setError(message);
        toast.error(message);
      } finally {
        if (requestIdRef.current === requestId) {
          setLoading(false);
        }
      }
    },
    [filters, pageSize, category],
  );

  // Cleanup no unmount — cancela request em voo.
  useEffect(() => () => controllerRef.current?.abort(), []);

  useEffect(() => {
    if (!enabled) return;
    // load() dispara setLoading(true) síncrono para spinner imediato;
    // não causa cascading render porque loading não está nas deps.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load(0);
  }, [enabled, load]);

  const removeLocal = useCallback((id: number) => {
    setData((prev) => prev.filter((item) => item.id !== id));
    setTotalElements((prev) => Math.max(0, prev - 1));
  }, []);

  const updateLocal = useCallback((updated: InvoiceResponseDTO) => {
    setData((prev) =>
      prev.map((item) =>
        item.id === updated.id
          ? {
              ...updated,
              // Só a busca popula contractNumber/clientName — respostas de
              // mutação vêm com null; preserva o que a linha já exibia.
              contractNumber: updated.contractNumber ?? item.contractNumber,
              clientName: updated.clientName ?? item.clientName,
            }
          : item,
      ),
    );
  }, []);

  const reload = useCallback(() => {
    load(page);
  }, [load, page]);

  return {
    data,
    loading,
    error,
    page,
    totalPages,
    totalElements,
    filters,
    setFilters,
    setPage: load,
    reload,
    removeLocal,
    updateLocal,
  };
}
